import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronLeft, FiChevronRight, FiBriefcase, FiUserPlus, FiSearch } from "react-icons/fi";
import { DEV_TOOL_ICON_MAP } from "../../data/devToolIcons";
import { SERVICE_ICON_MAP } from "../../data/serviceIcons";

// Slides feature seller services; slides made before that change may still hold developer-tool icons.
const ICON_MAP = { ...DEV_TOOL_ICON_MAP, ...SERVICE_ICON_MAP };

// The Co-Work banner: a slider built like the Home hero (image left, content right, typed title, icon
// pills) with two call-to-action buttons: "Register" for visitors / "Post a Job" for signed-in members, and
// "Find Seller". Slides are managed in the admin panel under Co-Work > Banner Slides; the button texts under
// Co-Work > Marketplace Content.
export default function CoWorkSlider({ slides, settings, fallbackTitle, fallbackText, user, onPrimary, onFindSeller }) {
  // `slides` is null while they load: show an empty banner instead of flashing the fallback slide.
  const list = slides && slides.length > 0
    ? slides
    : [{ id: "fallback", title: fallbackTitle || "Co-Work Marketplace", description: fallbackText || "", icons: "", image: null }];
  const [index, setIndex] = useState(0);
  const [typedTitle, setTypedTitle] = useState("");

  const goTo = useCallback((i) => setIndex(((i % list.length) + list.length) % list.length), [list.length]);

  useEffect(() => {
    if (list.length < 2) return;
    const interval = setInterval(() => goTo(index + 1), 7000);
    return () => clearInterval(interval);
  }, [list.length, index, goTo]);

  const current = list[index] || list[0];

  useEffect(() => {
    const text = current.title || "";
    setTypedTitle("");
    let i = 0;
    const typeInterval = setInterval(() => {
      i += 1;
      setTypedTitle(text.slice(0, i));
      if (i >= text.length) clearInterval(typeInterval);
    }, 70);
    return () => clearInterval(typeInterval);
  }, [current.title]);

  const icons = (current.icons || "").split(",").filter(Boolean);

  if (slides === null) return <section className="min-h-[620px] bg-navy border-b border-navy-border" aria-busy="true" />;

  return (
    <section className="relative min-h-[620px] flex items-center text-white border-b border-navy-border bg-navy overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="floating-light absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-accent/20 blur-[120px]" style={{ animationDuration: "18s" }} />
        <div className="floating-light absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-accent-light/10 blur-[120px]" style={{ animationDuration: "22s", animationDelay: "2s" }} />
        <div className="floating-light absolute top-1/3 left-1/2 w-[350px] h-[350px] rounded-full bg-purple-500/10 blur-[100px]" style={{ animationDuration: "26s", animationDelay: "4s" }} />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-16 lg:px-[75px] py-14">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className={`grid gap-12 items-center ${current.image ? "md:grid-cols-[40%_60%]" : ""}`}
          >
            {current.image && (
              <div className="flex items-center justify-center order-2 md:order-1">
                <img src={current.image} alt={current.title} className="w-full max-w-md aspect-square rounded-xl shadow-2xl object-cover" />
              </div>
            )}

            <div className="text-center md:text-left order-1 md:order-2">
              <span className="text-accent-light uppercase tracking-widest text-sm font-semibold">Co-Work Marketplace</span>
              <h2 className="mt-3 text-3xl md:text-5xl font-extrabold tracking-tight">
                <span className="gradient-text-animated-warm">{typedTitle}</span>
                <span className="typing-cursor inline-block w-[3px] md:w-[4px] ml-1 bg-accent-light align-middle" style={{ height: "0.85em" }} />
              </h2>
              {current.description && <p className="mt-6 text-lg text-gray-300">{current.description}</p>}

              {icons.length > 0 && (
                <div className="mt-6 flex flex-wrap justify-center md:justify-start gap-3">
                  {icons.map((key) => {
                    const entry = ICON_MAP[key];
                    if (!entry) return null;
                    const { Icon, label } = entry;
                    return (
                      <span key={key} title={label} className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-navy-border text-white/80 text-sm">
                        <Icon size={16} />
                        {label}
                      </span>
                    );
                  })}
                </div>
              )}

              <div className="mt-8 flex flex-wrap justify-center md:justify-start gap-3">
                <button
                  onClick={onPrimary}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 hover:shadow-accent/40 transition-shadow"
                >
                  {user ? <FiBriefcase /> : <FiUserPlus />} {user ? (settings?.buyer_button || "Post a Job") : (settings?.register_button || "Register")}
                </button>
                <button
                  onClick={onFindSeller}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-accent-light/60 text-white font-semibold hover:bg-accent-light/10 transition-colors"
                >
                  <FiSearch /> {settings?.find_seller_button || "Find Seller"}
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {list.length > 1 && (
          <div className="flex items-center justify-center gap-6 mt-10">
            <button onClick={() => goTo(index - 1)} aria-label="Previous slide" className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white hover:border-accent-light transition-colors">
              <FiChevronLeft />
            </button>
            <div className="flex gap-2">
              {list.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => goTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${i === index ? "bg-accent-light" : "bg-white/20 hover:bg-white/40"}`}
                />
              ))}
            </div>
            <button onClick={() => goTo(index + 1)} aria-label="Next slide" className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white hover:border-accent-light transition-colors">
              <FiChevronRight />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
